import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  FilterBar,
  Input,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { goodsReceiptPath, purchaseOrderPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { suppliersApi } from '../suppliers/suppliersApi';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrdersApi } from '../purchase-orders/purchaseOrdersApi';
import PoPickList from '../purchase-orders/PoPickList';
import { receivingApi, grnStatusMeta, grnDiscrepancyMeta, GRN_STATUSES } from './receivingApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 25;
export default function ReceivingPage() {
  const navigate = useNavigate();
  const t = useT();
  const { activePropertyId, hasPermission } = useProperty();
  const canRecord = hasPermission('receiving.record');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);
  const pickerDebounceRef = useRef(null);

  // "Record receipt" entry point: pick a receivable PO without going through the PO list.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const loadReceivablePos = useCallback(async (term) => {
    setPickerLoading(true);
    try {
      const res = await purchaseOrdersApi.list({
        statuses: 'Approved,Sent,PartiallyReceived',
        search: term || '',
        page: 1,
        pageSize: 50,
        sortBy: 'number',
        descending: true,
      });
      setPickerPos(res?.items || []);
    } catch {
      setPickerPos([]);
    } finally {
      setPickerLoading(false);
    }
  }, []);

  const openPicker = () => {
    setPickerOpen(true);
    setPickerSearch('');
    loadReceivablePos('');
  };

  const onPickerSearch = (value) => {
    setPickerSearch(value);
    if (pickerDebounceRef.current) clearTimeout(pickerDebounceRef.current);
    pickerDebounceRef.current = setTimeout(() => loadReceivablePos(value), 300);
  };

  const choosePo = (po) => {
    setPickerOpen(false);
    navigate(purchaseOrderPath(po, '/receive'));
  };

  useEffect(() => {
    let active = true;
    suppliersApi.list({ page: 1, pageSize: 200, sortBy: 'name' })
      .then((res) => { if (active) setSuppliers(res?.items || []); }).catch(() => {});
    catalogApi.listLocations(activePropertyId)
      .then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: GRN_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'supplierIds', label: t('Supplier'), type: 'multiselect', searchable: true, options: suppliers.map((s) => ({ value: s.id, label: s.name })) },
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'received', label: t('Received'), type: 'daterange', fromParam: 'receivedFrom', toParam: 'receivedTo' },
  ], [suppliers, locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await receivingApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load goods receipts.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const open = (row) => navigate(goodsReceiptPath(row));

  const columns = [
    { key: 'number', header: t('GRN'), width: 130, render: (g) => <span style={{ fontFamily: 'var(--font-mono)' }}>{g.number}</span> },
    {
      key: 'po', header: t('Purchase order'), width: 200,
      render: (g) => (
        <div>
          <div style={{ fontFamily: 'var(--font-mono)' }}>{g.purchaseOrderNumber || '—'}</div>
          <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{g.supplierName || t('No supplier')}</div>
        </div>
      ),
    },
    {
      key: 'status', header: t('Status'), width: 190,
      render: (g) => {
        const m = grnStatusMeta(g.status);
        const hasDisc = g.discrepancyStatus && g.discrepancyStatus !== 'None';
        const d = grnDiscrepancyMeta(g.discrepancyStatus);
        return (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge tone={m.tone} dot>{t(m.label)}</Badge>
            {g.hasOverReceipt && <Badge tone="warning">{t('Over')}</Badge>}
            {hasDisc && <Badge tone={d.tone}>{d.label === 'Open' ? t('Discrepancy') : t('Resolved')}</Badge>}
          </span>
        );
      },
    },
    { key: 'received', header: t('Received'), width: 120, render: (g) => (g.receivedDate ? new Date(g.receivedDate).toLocaleDateString() : '—') },
    { key: 'lines', header: t('Lines'), width: 70, align: 'right', render: (g) => g.lineCount },
    { key: 'total', header: t('Value'), width: 140, align: 'right', render: (g) => money(g.total) },
    { key: 'by', header: t('Received by'), width: 160, render: (g) => g.receivedByName || <span className="pp-cell-muted">—</span> },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Receiving')}
        description={t('Goods receipts against issued purchase orders. Posting a receipt updates stock on hand and purchase history.')}
        icon={<Icon.ArrowDownCircle size={22} />}
        actions={canRecord ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openPicker}>
            {t('Record receipt')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by GRN, PO or supplier…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load goods receipts")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(g) => g.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ArrowDownCircle size={28} />}
                title={hasFilters ? t('No matching goods receipts') : t('No goods receipts yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Open a sent purchase order and choose “Receive goods” to record a delivery.')}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}

      <Drawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('Record a receipt')}
        description={t("Pick the purchase order you're receiving against. Only approved orders awaiting delivery are shown.")}
        size="lg"
      >
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <Input
            value={pickerSearch}
            onChange={(e) => onPickerSearch(e.target.value)}
            placeholder={t('Search by PO number or supplier…')}
            leadingIcon={<Icon.Search size={15} />}
            autoFocus
          />
        </div>
        {pickerLoading ? (
          <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-10)' }}><Spinner size={22} /></div>
        ) : pickerPos.length === 0 ? (
          <EmptyState
            icon={<Icon.ShoppingCart size={26} />}
            title={t('No orders awaiting delivery')}
            description={pickerSearch ? t('No approved orders match your search.') : t('Approve a purchase order first, then receive against it here.')}
          />
        ) : (
          <PoPickList items={pickerPos} onPick={choosePo} />
        )}
      </Drawer>
    </PageContainer>
  );
}
